// Competition page shell (UI-SPEC §6 — K1 Hero + Section-Nav + Overview;
// states K7 loading / K8 error). V1 sections: Overview · Standings/Bracket ·
// Schedule · Stats. Best XI / History exist as constants (competitionsUtils
// SECTIONS) but are NOT rendered (V2). Sections without data are absent from
// the nav AND the page ("empty renders nothing", K3: fewer sections, no
// message — K8 error is distinct and offers retry).
//
// Data = the merged BE V1 API (CONTRACTS.md):
//   GET /api/competitions/<id>/                    detail/hero + seasons
//   GET /api/standings/<season_id>/                standings/groups + zones
//   GET /api/cups/<id>/?season_id=                 bracket (cups only)
//   GET /api/competitions/<id>/matches/?season_id= stage/round schedule
//   GET /api/competitions/<id>/leaderboards/       boards + rating board
// The full section bodies are the sister-agent embeddables under
// src/components/competitions/ (props only); this page fetches, maps
// (competitionsUtils) and mounts them.
//
// Mobile behaviour (declared per CLAUDE.md UI rule 1): hero stacks and the
// season switcher goes full-width; the section-nav row scrolls horizontally
// (never cut, never wrapped); overview cards stack single-column; the
// embeddables carry their own declared mobile patterns (K9).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { dashboardService } from "../../services/api";
import tpIcon from "../../assets/images/tp-icon.png";
import { ratingTier10 } from "../../components/totw/totwUtils";
import CompetitionStandingsTable from "../../components/competitions/CompetitionStandingsTable";
import CompetitionGroups from "../../components/competitions/CompetitionGroups";
import CompetitionBracket from "../../components/competitions/CompetitionBracket";
import CompetitionScheduleList from "../../components/competitions/CompetitionScheduleList";
import CompetitionStatsSection from "../../components/competitions/CompetitionStatsSection";
import {
  SECTIONS,
  availableSections,
  standingsLabelIsBracket,
  competitionMonogram,
  competitionName,
  formatMatchDate,
  formatMatchTime,
  flattenSchedule,
  defaultRound,
  mapMatchToScheduleRow,
  mapCupThirdPlace,
  mapCupToBracketRounds,
  mapLeaderboardsToStatsProps,
  promotionsFromStandings,
  profilePathFor,
  hasProfileTarget,
} from "./competitionsUtils";
import { canonicalPromotion } from "../../components/competitions/shared/competitionsZones";
import { useCompetitionsTr } from "../../components/competitions/shared/useCompetitionsTr";
import "./competitions.css";

// UI-SPEC §22: competition rating boards show TWO decimals ("8.34"). Same
// null/zero gating as totwUtils.formatRating10; tier colour stays with the
// single-source ratingTier10.
const formatRating2 = (v) => {
  if (v == null || v === "") return null;
  const value = Number(v);
  if (!Number.isFinite(value) || value <= 0) return null;
  return value.toFixed(2);
};

const ChevronDown = ({ stroke = "#C7D6EC" }) => (
  <svg width="11" height="7" viewBox="0 0 12 8" fill="none" aria-hidden="true">
    <path d="M1 1.5L6 6.5L11 1.5" stroke={stroke} strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

// Nav labels (EN source; DE via the static bundle: Übersicht · Tabelle /
// Turnierbaum · Spielplan · Statistik).
const SECTION_LABELS = {
  [SECTIONS.OVERVIEW]: "Overview",
  [SECTIONS.STANDINGS]: "Standings",
  [SECTIONS.SCHEDULE]: "Schedule",
  [SECTIONS.STATS]: "Stats",
  // V2 — designed, not rendered:
  [SECTIONS.BEST_XI]: "Best XI",
  [SECTIONS.HISTORY]: "History",
};

const SeasonSwitcher = ({ seasons, selected, onSelect }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);
  if (!Array.isArray(seasons) || seasons.length === 0 || !selected?.year) {
    return null;
  }
  const historical = selected.is_current === false;
  return (
    <div className="tpcomp-season" ref={wrapRef}>
      <button
        type="button"
        className={`tpcomp-season-btn${historical ? " tpcomp-season-historical" : ""}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="tpcomp-tnum">{`Season ${selected.year}`}</span>
        <ChevronDown stroke={historical ? "#fff" : "#C7D6EC"} />
      </button>
      {open && (
        <div className="tpcomp-season-menu" role="listbox">
          {seasons.map((s) => (
            <button
              key={s.id}
              type="button"
              role="option"
              aria-selected={s.id === selected.id}
              className="tpcomp-season-option tpcomp-tnum"
              onClick={() => {
                setOpen(false);
                if (s.id !== selected.id) onSelect(s);
              }}
            >
              {`Season ${s.year}`}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Hero = ({ competition, country, season, seasons, meta, onSeasonSelect }) => {
  const [logoErrored, setLogoErrored] = useState(false);
  const name = competitionName(competition);
  const metaParts = (meta || []).filter(Boolean);
  return (
    <div className="tpcomp-hero">
      {/* Watermark clipping lives on THIS layer, not on the hero itself: an
          overflow:hidden hero would clip the season dropdown at the hero's
          bottom edge (QA2 defect 1 — root cause, not a z-index patch). */}
      <span className="tpcomp-hero-clip" aria-hidden="true">
        <img className="tpcomp-hero-watermark" src={tpIcon} alt="" />
      </span>
      <div className="tpcomp-hero-row">
        <div className="tpcomp-hero-main">
          <span className="tpcomp-hero-logo" aria-hidden="true">
            {competition?.logo && !logoErrored ? (
              <img src={competition.logo} alt="" onError={() => setLogoErrored(true)} />
            ) : (
              competitionMonogram(competition)
            )}
          </span>
          <div style={{ minWidth: 0 }}>
            {name ? <h1 className="tpcomp-hero-name">{name}</h1> : null}
            {metaParts.length > 0 && (
              <div className="tpcomp-hero-meta tpcomp-tnum">
                {country?.logo && (
                  <img
                    src={country.logo}
                    alt=""
                    width={20}
                    height={14}
                    style={{ objectFit: "cover", borderRadius: 1, flex: "none" }}
                  />
                )}
                {metaParts.map((part, i) => (
                  <span key={part} style={{ display: "inline-flex", gap: 10 }}>
                    {i > 0 && <span className="tpcomp-hero-sep">·</span>}
                    <span>{part}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        <SeasonSwitcher seasons={seasons} selected={season} onSelect={onSeasonSelect} />
      </div>
    </div>
  );
};

const SectionNav = ({ sections, labels, active, onJump }) => {
  if (!sections || sections.length === 0) return null;
  return (
    <nav className="tpcomp-nav" aria-label="Competition sections">
      {sections.map((id) => (
        <button
          key={id}
          type="button"
          className={`tpcomp-nav-tab${active === id ? " tpcomp-nav-active" : ""}`}
          onClick={() => onJump(id)}
        >
          {labels[id]}
        </button>
      ))}
    </nav>
  );
};

const OverviewCard = ({ title, linkLabel, onLink, children, footer }) => (
  <div className="tpcomp-ov-card">
    <div className="tpcomp-ov-card-head">
      <span className="tpcomp-ov-card-title">{title}</span>
      {linkLabel && (
        <button type="button" className="tpcomp-ov-card-link" onClick={onLink}>
          {linkLabel}
        </button>
      )}
    </div>
    {children}
    {footer}
  </div>
);

const PlayerMiniRow = ({ rank, row, valueNode, onOpen }) => (
  <div
    className={`tpcomp-ov-board-row${onOpen ? " tpcomp-ov-click" : ""}`}
    onClick={onOpen}
  >
    <span className="tpcomp-ov-pos tpcomp-tnum">{rank}</span>
    <span className="tpcomp-ov-player">
      <span className="tpcomp-ov-photo">
        {row?.player?.logo ? <img src={row.player.logo} alt="" /> : null}
      </span>
      <span style={{ minWidth: 0 }}>
        <span className="tpcomp-ov-player-name" style={{ display: "block" }}>
          {row?.player?.name}
        </span>
        {(row?.teams?.[0]?.name || row?.player?.position) && (
          <span className="tpcomp-ov-player-sub" style={{ display: "block" }}>
            {[row?.teams?.[0]?.name, row?.player?.position]
              .filter(Boolean)
              .join(" · ")}
          </span>
        )}
      </span>
    </span>
    {valueNode}
  </div>
);

const CompetitionPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { tr } = useCompetitionsTr();
  const [seasonId, setSeasonId] = useState(null);
  const [state, setState] = useState({
    loading: true,
    error: false,
    detail: null,
    standings: null,
    bracket: null,
    matches: null,
    leaderboards: null,
  });
  const [activeSection, setActiveSection] = useState(SECTIONS.OVERVIEW);
  const [scheduleRound, setScheduleRound] = useState(null);
  const abortRef = useRef(null);
  const sectionRefs = useRef({});

  const load = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    const { signal } = controller;
    abortRef.current = controller;
    setState((prev) => ({ ...prev, loading: true, error: false }));
    try {
      const detailRes = await dashboardService.getCompetitionDetail(id, { signal });
      if (signal.aborted) return;
      if (!detailRes.success || !detailRes.data?.competition?.id) {
        setState((prev) => ({ ...prev, loading: false, error: true }));
        return;
      }
      const detail = detailRes.data;
      const seasons = detail.seasons || [];
      const selected =
        (seasonId && seasons.find((s) => s.id === seasonId)) ||
        (detail.cur_season?.id && detail.cur_season) ||
        seasons.find((s) => s.is_current) ||
        seasons[0] ||
        null;

      const isCup = detail.competition?.type === 2;
      const [standingsRes, bracketRes, matchesRes, boardsRes] =
        await Promise.allSettled([
          selected?.has_table && selected?.id
            ? dashboardService.getSeasonStandings(selected.id, { signal })
            : Promise.resolve(null),
          isCup
            ? dashboardService.getCupBracket(id, {
                seasonId: selected?.id,
                signal,
              })
            : Promise.resolve(null),
          dashboardService.getCompetitionMatches(id, {
            seasonId: selected?.id,
            signal,
          }),
          dashboardService.getCompetitionLeaderboards(id, {
            seasonId: selected?.id,
            limit: 25,
            signal,
          }),
        ]);
      if (signal.aborted) return;
      const value = (res) =>
        res.status === "fulfilled" && res.value?.success ? res.value.data : null;
      setState({
        loading: false,
        error: false,
        detail: { ...detail, selectedSeason: selected },
        standings: value(standingsRes),
        bracket: value(bracketRes),
        matches: value(matchesRes),
        leaderboards: value(boardsRes),
      });
      setScheduleRound(null); // re-derive for the new season
    } catch (e) {
      if (e?.name === "CanceledError" || e?.name === "AbortError") return;
      setState((prev) => ({ ...prev, loading: false, error: true }));
    }
  }, [id, seasonId]);

  useEffect(() => {
    load();
    return () => abortRef.current?.abort();
  }, [load]);

  const detail = state.detail;
  const competition = detail?.competition || null;
  const country = detail?.country || null;
  const season = detail?.selectedSeason || null;
  const seasons = detail?.seasons || [];

  // ── Derived section data (mapped once per payload) ──────────────────────
  const bracketRounds = useMemo(
    () => mapCupToBracketRounds(state.bracket),
    [state.bracket],
  );
  const bracketThirdPlace = useMemo(
    () => mapCupThirdPlace(state.bracket),
    [state.bracket],
  );
  const schedule = useMemo(() => {
    const { rounds, byRound, timesById } = flattenSchedule(state.matches);
    return rounds.length ? { rounds, byRound, timesById } : null;
  }, [state.matches]);
  const stats = useMemo(
    () =>
      state.leaderboards ? mapLeaderboardsToStatsProps(state.leaderboards) : null,
    [state.leaderboards],
  );
  const promotions = useMemo(
    () => promotionsFromStandings(state.standings),
    [state.standings],
  );

  const sections = useMemo(
    () =>
      availableSections({
        standings: state.standings,
        bracketRounds,
        schedule,
        stats,
      }),
    [state.standings, bracketRounds, schedule, stats],
  );
  const bracketMode = useMemo(
    () => standingsLabelIsBracket({ standings: state.standings, bracketRounds }),
    [state.standings, bracketRounds],
  );
  const labels = useMemo(
    () => ({
      ...SECTION_LABELS,
      // Pure KO cup: "NAV LABEL BECOMES “BRACKET”, NO TABLE" (K2).
      [SECTIONS.STANDINGS]: bracketMode
        ? "Bracket"
        : SECTION_LABELS[SECTIONS.STANDINGS],
    }),
    [bracketMode],
  );

  const activeRound =
    scheduleRound ??
    (schedule ? defaultRound(schedule.rounds, schedule.byRound) : null);
  const scheduleRows = useMemo(() => {
    if (!schedule || activeRound == null) return [];
    return (schedule.byRound.get(activeRound) || []).map((m) =>
      mapMatchToScheduleRow(m, schedule.timesById),
    );
  }, [schedule, activeRound]);

  const jumpTo = (sectionId) => {
    setActiveSection(sectionId);
    sectionRefs.current[sectionId]?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  // Default row action = profile navigation (build brief §2.2), resolved via
  // the Bestand routes (profilePathFor: TOTW's /search/player/<wyscout_id>,
  // club cards' /search/club/<id>) — ONLY when a platform id exists (never
  // invented links; TS-ids are not platform ids). `hasProfileTarget` gates
  // the embeddables' affordance so unresolvable rows show no dead cursor.
  const onNavigateEntity = useCallback(
    (payload) => {
      const path = profilePathFor(payload);
      if (path) navigate(path);
    },
    [navigate],
  );

  // ── Hero meta ("Empty renders nothing": absent fields simply absent) ────
  const meta = useMemo(() => {
    if (!detail) return [];
    const parts = [];
    if (country?.name) parts.push(country.name);
    const teams = state.standings?.standings?.length || null;
    if (teams) parts.push(`${teams} teams`);
    const isCurrent = season?.is_current !== false;
    if (!isCurrent) {
      parts.push("Season complete");
    } else if (bracketMode) {
      parts.push("Knockout phase");
    } else if (competition?.cur_round && competition?.round_count) {
      parts.push(`Matchday ${competition.cur_round} of ${competition.round_count}`);
    }
    return parts;
  }, [detail, country, state.standings, season, bracketMode, competition]);

  // ── Overview data ───────────────────────────────────────────────────────
  const standingsTop5 = useMemo(
    () => (state.standings?.standings || []).slice(0, 5),
    [state.standings],
  );
  const nextFixtures = useMemo(() => {
    if (!schedule) return [];
    const upcoming = [];
    schedule.rounds.forEach((r) => {
      (schedule.byRound.get(r.round) || []).forEach((m) => {
        if (!(m?.score && m.score.home != null)) {
          upcoming.push({ ...m, _round: r.round });
        }
      });
    });
    upcoming.sort((a, b) => (a.match_time || 0) - (b.match_time || 0));
    return upcoming.slice(0, 5);
  }, [schedule]);
  // Matchday prefix only for NUMBERED rounds — fixtures from the unnumbered
  // cup bucket (flattenSchedule's UNNUMBERED_ROUND sentinel) keep the plain
  // "Next fixtures" title instead of "Matchday unnumbered".
  const nextRoundNum = Number.isFinite(nextFixtures[0]?._round)
    ? nextFixtures[0]._round
    : null;
  const topScorers = useMemo(
    () => (state.leaderboards?.boards?.top_scorers || []).slice(0, 3),
    [state.leaderboards],
  );
  const topRatings = useMemo(
    () =>
      (state.leaderboards?.boards?.top_rated || [])
        .filter((r) => Number(r?.rating) > 0)
        .slice(0, 3),
    [state.leaderboards],
  );

  // ── K7 loading ──────────────────────────────────────────────────────────
  if (state.loading) {
    return (
      <div className="tpcomp-page" data-testid="competition-page-skeleton">
        <div className="tpcomp-hero tpcomp-hero-skel">
          <div className="tpcomp-hero-row">
            <div className="tpcomp-hero-main">
              <span
                className="tpcomp-skel-onnavy"
                style={{ width: 92, height: 92, borderRadius: 4, flex: "none" }}
              />
              <div>
                <div className="tpcomp-skel-onnavy" style={{ width: 340, height: 32 }} />
                <div
                  className="tpcomp-skel-onnavy"
                  style={{ width: 260, height: 14, marginTop: 14, opacity: 0.7 }}
                />
              </div>
            </div>
            <div className="tpcomp-skel-onnavy" style={{ width: 180, height: 44 }} />
          </div>
        </div>
        <div className="tpcomp-nav" style={{ gap: 26, padding: "16px 48px" }}>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <span key={i} className="tpcomp-skel" style={{ width: 76, height: 14, flex: "none" }} />
          ))}
        </div>
        <div className="tpcomp-content">
          <div className="tpcomp-ov-card">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                style={{
                  display: "grid",
                  gridTemplateColumns: "30px 1fr 60px 60px",
                  gap: 12,
                  alignItems: "center",
                  padding: "0 18px",
                  minHeight: 48,
                  borderBottom: i < 7 ? "1px solid #F4F7FA" : "none",
                }}
              >
                <span className="tpcomp-skel" style={{ width: 26, height: 26 }} />
                <span className="tpcomp-skel" style={{ width: 200, height: 13 }} />
                <span className="tpcomp-skel-soft" style={{ height: 12 }} />
                <span className="tpcomp-skel-soft" style={{ height: 12 }} />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ── K8 error (hero stays when we know the competition; code names it) ───
  if (state.error) {
    const errCode = season?.year
      ? `ERR-COMP-SEASON-${season.year}`
      : `ERR-COMP-${id}`;
    return (
      <div className="tpcomp-page">
        {competition && (
          <Hero
            competition={competition}
            country={country}
            season={season}
            seasons={[]}
            meta={meta}
            onSeasonSelect={() => {}}
          />
        )}
        <div className="tpcomp-content">
          <div className="tpcomp-error" role="alert">
            <div className="tpcomp-error-title" style={{ fontSize: 16 }}>
              Standings and schedule could not be loaded
            </div>
            <div className="tpcomp-error-copy">
              The competition data feed did not answer. This is a load failure,
              not an empty season · the sections stay in the nav and reappear
              on a successful retry.
            </div>
            <div className="tpcomp-error-actions">
              <button type="button" className="tpcomp-retry" onClick={load}>
                Retry
              </button>
              <span className="tpcomp-error-code">{errCode}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const groups = state.standings?.groups;

  return (
    <div className="tpcomp-page">
      <Hero
        competition={competition}
        country={country}
        season={season}
        seasons={seasons}
        meta={meta}
        onSeasonSelect={(s) => setSeasonId(s.id)}
      />
      <SectionNav
        sections={sections}
        labels={labels}
        active={activeSection}
        onJump={jumpTo}
      />
      <div className="tpcomp-content">
        {/* ── Overview (K1 §6.3) ───────────────────────────────────────── */}
        {sections.includes(SECTIONS.OVERVIEW) && (
          <section
            ref={(el) => { sectionRefs.current[SECTIONS.OVERVIEW] = el; }}
            className="tpcomp-k-section"
            aria-label="Overview"
          >
            <div className="tpcomp-ov-row1">
              {standingsTop5.length > 0 && (
                <OverviewCard
                  title="Standings"
                  linkLabel="Full table"
                  onLink={() => jumpTo(SECTIONS.STANDINGS)}
                  footer={
                    promotions.length > 0 ? (
                      <div className="tpcomp-ov-legend">
                        {promotions.map((p) => (
                          <span className="tpcomp-ov-legend-item" key={p.id || p.name}>
                            <span
                              className="tpcomp-ov-swatch"
                              style={{ background: p.color }}
                            />
                            {tr(p.name)}
                          </span>
                        ))}
                      </div>
                    ) : null
                  }
                >
                  {standingsTop5.map((row) => (
                    <div
                      className={`tpcomp-ov-standing-row${
                        hasProfileTarget(row) ? " tpcomp-ov-click" : ""
                      }`}
                      key={row.team?.id || row.position}
                      onClick={
                        hasProfileTarget(row)
                          ? () => onNavigateEntity(row)
                          : undefined
                      }
                    >
                      <span
                        className="tpcomp-zonekey"
                        style={{
                          background:
                            canonicalPromotion(row.promotion)?.color ||
                            "transparent",
                        }}
                      />
                      <span className="tpcomp-ov-pos tpcomp-tnum">{row.position}</span>
                      <span className="tpcomp-ov-team">
                        {row.team?.logo && (
                          <img
                            src={row.team.logo}
                            alt=""
                            width={22}
                            height={22}
                            style={{ objectFit: "contain", flex: "none" }}
                          />
                        )}
                        <span className="tpcomp-ov-team-name">
                          {row.team?.name || row.team?.short_name}
                        </span>
                      </span>
                      <span className="tpcomp-ov-goals tpcomp-tnum">
                        {row.goals_for != null && row.goals_against != null
                          ? `${row.goals_for}:${row.goals_against}`
                          : null}
                      </span>
                      <span className="tpcomp-ov-pts tpcomp-tnum">{row.points}</span>
                    </div>
                  ))}
                </OverviewCard>
              )}
              {nextFixtures.length > 0 && (
                <OverviewCard
                  title={
                    nextRoundNum != null
                      ? `Matchday ${nextRoundNum} · next fixtures`
                      : "Next fixtures"
                  }
                  linkLabel="Schedule"
                  onLink={() => jumpTo(SECTIONS.SCHEDULE)}
                >
                  {nextFixtures.map((m) => (
                    <div className="tpcomp-ov-fixture-row" key={m.id}>
                      <span className="tpcomp-ov-fx-date tpcomp-tnum">
                        {formatMatchDate(m.match_time)}
                      </span>
                      <span className="tpcomp-ov-fx-home">
                        {m.home?.name || m.home?.short_name}
                      </span>
                      <span className="tpcomp-ov-fx-time tpcomp-tnum">
                        {formatMatchTime(m.match_time, m.tbd)}
                      </span>
                      <span className="tpcomp-ov-fx-away">
                        {m.away?.name || m.away?.short_name}
                      </span>
                    </div>
                  ))}
                </OverviewCard>
              )}
            </div>
            {(topScorers.length > 0 || topRatings.length > 0) && (
              <div className="tpcomp-ov-row2">
                {topScorers.length > 0 && (
                  <OverviewCard
                    title="Top scorers"
                    linkLabel="All boards"
                    onLink={() => jumpTo(SECTIONS.STATS)}
                  >
                    {topScorers.map((row, i) => (
                      <PlayerMiniRow
                        key={row.player?.id || i}
                        rank={i + 1}
                        row={row}
                        onOpen={
                          hasProfileTarget({ type: "player", row })
                            ? () => onNavigateEntity({ type: "player", row })
                            : undefined
                        }
                        valueNode={
                          <span className="tpcomp-ov-value tpcomp-tnum">
                            {row.goals}
                          </span>
                        }
                      />
                    ))}
                  </OverviewCard>
                )}
                {/* Top ratings — DATA-PERMITTING: absent without a season
                    rating source (K4), no layout gap left behind. */}
                {topRatings.length > 0 && (
                  <OverviewCard
                    title="Top ratings"
                    linkLabel="All boards"
                    onLink={() => jumpTo(SECTIONS.STATS)}
                  >
                    {topRatings.map((row, i) => {
                      const ratingLabel = formatRating2(row.rating);
                      return (
                        <PlayerMiniRow
                          key={row.player?.id || i}
                          rank={i + 1}
                          row={row}
                          onOpen={
                            hasProfileTarget({ type: "player", row })
                              ? () => onNavigateEntity({ type: "player", row })
                              : undefined
                          }
                          valueNode={
                            ratingLabel ? (
                              <span
                                className="tpcomp-rating-chip tpcomp-tnum"
                                style={{ background: ratingTier10(row.rating).solid }}
                              >
                                {ratingLabel}
                              </span>
                            ) : (
                              <span />
                            )
                          }
                        />
                      );
                    })}
                  </OverviewCard>
                )}
              </div>
            )}
          </section>
        )}

        {/* ── Standings / Groups / Bracket (embeddables) ────────────────── */}
        {sections.includes(SECTIONS.STANDINGS) && (
          <section
            ref={(el) => { sectionRefs.current[SECTIONS.STANDINGS] = el; }}
            className="tpcomp-k-section"
            aria-label={labels[SECTIONS.STANDINGS]}
          >
            <div className="tpcomp-k-head">
              <h2 className="tpcomp-k-title">{labels[SECTIONS.STANDINGS]}</h2>
              <span className="tpcomp-section-line" />
            </div>
            {/* K2b: group phase first, both blocks stacked. */}
            {Array.isArray(groups) && groups.length > 0 ? (
              <CompetitionGroups
                groups={groups.map((g) => ({ name: g.name, rows: g.standings }))}
                promotions={promotions}
                onNavigate={onNavigateEntity}
                navigable={hasProfileTarget}
              />
            ) : (
              (state.standings?.standings || []).length > 0 && (
                <CompetitionStandingsTable
                  rows={state.standings.standings}
                  promotions={promotions}
                  onNavigate={onNavigateEntity}
                  navigable={hasProfileTarget}
                />
              )
            )}
            {bracketRounds.length > 0 && (
              <CompetitionBracket
                rounds={bracketRounds}
                thirdPlace={bracketThirdPlace}
                onNavigate={onNavigateEntity}
                navigable={hasProfileTarget}
              />
            )}
          </section>
        )}

        {/* ── Schedule (embeddable) ─────────────────────────────────────── */}
        {sections.includes(SECTIONS.SCHEDULE) && (
          <section
            ref={(el) => { sectionRefs.current[SECTIONS.SCHEDULE] = el; }}
            className="tpcomp-k-section"
            aria-label={SECTION_LABELS[SECTIONS.SCHEDULE]}
          >
            <CompetitionScheduleList
              title={SECTION_LABELS[SECTIONS.SCHEDULE]}
              rounds={schedule.rounds}
              round={activeRound}
              onRoundChange={setScheduleRound}
              matches={scheduleRows}
            />
          </section>
        )}

        {/* ── Stats (embeddable) ────────────────────────────────────────── */}
        {sections.includes(SECTIONS.STATS) && (
          <section
            ref={(el) => { sectionRefs.current[SECTIONS.STATS] = el; }}
            className="tpcomp-k-section"
            aria-label={SECTION_LABELS[SECTIONS.STATS]}
          >
            <CompetitionStatsSection
              rating={stats.rating}
              playerBoards={stats.playerBoards}
              onNavigate={onNavigateEntity}
              navigable={hasProfileTarget}
            />
          </section>
        )}
      </div>
    </div>
  );
};

export default CompetitionPage;
